import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layers, Minus, RefreshCw, ExternalLink, Trash2, LifeBuoy } from 'lucide-react';
import { publicApi, stackApi } from '../utils/api';
import { buildToolGoUrl } from '../utils/affiliateLinks';
import { useAuth } from '../contexts/AuthContext';
import {
  parseGuestStack,
  removeGuestStackTool,
  resolveItemId,
  saveGuestStack,
  normalizeIdentifier,
} from '../utils/runningStackStore';

const normalize = (value = '') => String(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const RunningStackDashboardModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [busyToolId, setBusyToolId] = useState(null);
  const [toolCatalog, setToolCatalog] = useState([]);

  const loadCatalog = useCallback(async () => {
    try {
      const response = await publicApi.getAllTools(false);
      setToolCatalog(response.data || []);
    } catch {
      setToolCatalog([]);
    }
  }, []);

  const loadStack = useCallback(async () => {
    setLoading(true);
    try {
      if (!isAuthenticated) {
        setItems(parseGuestStack());
      } else {
        const response = await stackApi.getDashboardStack();
        setItems(response.data?.items || []);
      }
    } catch {
      setItems(parseGuestStack());
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadStack();
    loadCatalog();
  }, [loadStack, loadCatalog]);

  useEffect(() => {
    const onStorage = () => loadStack();
    const onStackUpdated = () => loadStack();
    window.addEventListener('storage', onStorage);
    window.addEventListener('wowrolla:stack-updated', onStackUpdated);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('wowrolla:stack-updated', onStackUpdated);
    };
  }, [loadStack]);

  useEffect(() => {
    if (!open || !isAuthenticated) return undefined;

    loadStack();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadStack();
      }
    }, 120000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [open, isAuthenticated, loadStack]);

  const clearStack = async () => {
    const ids = Array.from(new Set(items.map((item) => resolveItemId(item)).filter(Boolean)));
    if (!ids.length) {
      if (!isAuthenticated) {
        saveGuestStack([]);
        setItems([]);
        window.dispatchEvent(new Event('wowrolla:stack-updated'));
      }
      return;
    }

    setBusyToolId('clearing');
    try {
      if (!isAuthenticated) {
        saveGuestStack([]);
        setItems([]);
      } else {
        await Promise.allSettled(ids.map((toolId) => stackApi.removeStackItem(toolId)));
        const refreshed = await stackApi.getDashboardStack();
        const remaining = refreshed.data?.items || [];
        const remainingIds = Array.from(new Set(remaining.map((item) => resolveItemId(item)).filter(Boolean)));
        if (remainingIds.length) {
          await Promise.allSettled(remainingIds.map((toolId) => stackApi.removeStackItem(toolId)));
        }
      }
      window.dispatchEvent(new Event('wowrolla:stack-updated'));
      await loadStack();
    } finally {
      setBusyToolId(null);
    }
  };

  const removeTool = async (toolId) => {
    if (!toolId) return;
    const normalizedId = normalizeIdentifier(toolId);
    setBusyToolId(normalizedId);
    try {
      if (!isAuthenticated) {
        const next = removeGuestStackTool(parseGuestStack(), normalizedId);
        saveGuestStack(next);
        setItems(next);
      } else {
        await stackApi.removeStackItem(normalizedId);
      }
      window.dispatchEvent(new Event('wowrolla:stack-updated'));
      await loadStack();
    } finally {
      setBusyToolId(null);
    }
  };

  const getBuyUrl = useCallback((item) => {
    if (item?.slug) {
      const maskedFromItem = buildToolGoUrl(item);
      if (maskedFromItem !== '#') return maskedFromItem;
    }

    const byId = toolCatalog.find((tool) => tool.id === resolveItemId(item));
    if (byId) return buildToolGoUrl(byId);

    const slug = item?.slug;
    if (slug) {
      const bySlug = toolCatalog.find((tool) => tool.slug === slug);
      if (bySlug) return buildToolGoUrl(bySlug);
    }

    const byName = toolCatalog.find((tool) => normalize(tool.name) === normalize(item.name));
    if (byName) return buildToolGoUrl(byName);
    
    return null;
  }, [toolCatalog]);

  const buyAll = () => {
    const urls = items
      .map((item) => getBuyUrl(item))
      .filter(Boolean);

    urls.forEach((url, index) => {
      window.setTimeout(() => {
        window.open(url, '_blank', 'noopener,noreferrer');
      }, index * 500);
    });
  };

  const totalMonthly = useMemo(
    () => (items || []).reduce((sum, item) => sum + Number(item.monthly_cost || 0), 0),
    [items],
  );

  if (location.pathname === '/find') {
    return null;
  }

  return (
    <div className="fixed top-[72px] right-4 z-[75] pointer-events-none">
      <div className="pointer-events-auto relative">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="border border-white/20 bg-[#090B10]/95 backdrop-blur-md shadow-2xl px-3 py-2 inline-flex items-center gap-2"
        >
          <Layers size={14} className="text-cyber-yellow" />
          <span className="text-white font-semibold text-xs">My Tool Dashboard</span>
          <span className="text-xs text-[#A0A0A5]">{loading ? <RefreshCw size={12} className="animate-spin" /> : `${items.length} tools`}</span>
        </button>

        {open && (
          <div className="absolute right-0 mt-2 w-[360px] max-w-[calc(100vw-1rem)] border border-white/20 bg-[#090B10]/97 backdrop-blur-md shadow-2xl">
            <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-2 text-white font-semibold text-sm">
                <Layers size={14} className="text-cyber-yellow" />
                Running Stack
              </span>
              <span className="text-xs text-[#A0A0A5]">${totalMonthly.toFixed(2)}/mo</span>
            </div>

            <div className="px-3 py-2 max-h-[240px] overflow-y-auto space-y-2">
              {items.length === 0 && (
                <span className="text-xs text-[#A0A0A5]">No tools in your running stack yet.</span>
              )}
              {items.map((item) => {
                const itemId = resolveItemId(item);
                const buyUrl = getBuyUrl(item);
                return (
                  <div key={itemId || item.name} className="inline-flex w-full items-center justify-between gap-2 border border-white/15 px-2 py-1 bg-white/[0.02]">
                    <span className="text-xs text-white max-w-[170px] truncate" title={item.name}>{item.name}</span>
                    <div className="inline-flex items-center gap-1">
                      {buyUrl && (
                        <a
                          href={buyUrl}
                          target="_blank"
                          rel="sponsored nofollow noopener noreferrer"
                          className="h-6 w-6 inline-flex items-center justify-center border border-cyber-yellow/40 text-cyber-yellow bg-transparent hover:bg-cyber-yellow/10"
                          title={`Buy ${item.name}`}
                        >
                          <ExternalLink size={12} />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => removeTool(itemId)}
                        disabled={busyToolId === itemId || busyToolId === 'clearing'}
                        className="h-6 w-6 inline-flex items-center justify-center border border-red-400/40 text-red-300 hover:bg-red-500/10 disabled:opacity-60"
                        title="Remove from stack"
                      >
                        <Minus size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-3 py-2 border-t border-white/10 flex gap-2 text-xs">
              <button
                type="button"
                onClick={buyAll}
                disabled={!items.length}
                className="px-2 py-1 border border-cyber-yellow/40 text-cyber-yellow bg-transparent hover:bg-cyber-yellow/10 disabled:opacity-50"
              >
                Buy
              </button>
              <button
                type="button"
                onClick={() => navigate('/consulting')}
                className="px-2 py-1 border border-laser-green/40 text-laser-green bg-transparent hover:bg-laser-green/10 inline-flex items-center justify-center gap-1"
              >
                <LifeBuoy size={12} /> Consult
              </button>
              <button
                type="button"
                onClick={clearStack}
                disabled={!items.length || busyToolId === 'clearing'}
                className="px-2 py-1 border border-red-400/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50 inline-flex items-center justify-center gap-1"
              >
                <Trash2 size={12} /> Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RunningStackDashboardModal;
